import { FormActionButtons } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import {
  Bleed,
  Box,
  Button,
  Heading,
  IconAdd,
  Inline,
  Stack,
} from 'braid-design-system';
import { useState } from 'react';
import { v4 as createGuid } from 'uuid';

import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useOnFormDirtyChange } from 'src/components/FormDrawer/useOnFormDirtyChange';
import { TaxonomyOptionsErrorAlert } from 'src/components/Profile/TaxonomyOptionsErrorAlert/TaxonomyOptionsErrorAlert';
import { RIGHT_TO_WORK } from 'src/components/Router';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';
import type {
  CountryInformation,
  RightToWorkType2Input,
} from 'src/types/graphql';

import { useRightsToWork, useUpdateRightsToWork } from '../data';
import type { HandleUpdateRightsToWorkProps } from '../types';
import {
  type RightToWorkTrackingData,
  useUpdateRightsToWorkAnalytics,
} from '../useUpdateRightsToWorkAnalytics';
import {
  mapRightsToWorkIntoInputs,
  removeDisplayedRightToWork,
  updateDisplayedRightToWork,
} from '../utils';

import translations from './.vocab';
import { RightToWorkForm } from './RightToWorkForm/RightToWorkForm';
import { useRightToWorkOptionsQuery } from './RightToWorkItem/RightToWorkDropdown/data';
import { RightToWorkItem } from './RightToWorkItem/RightToWorkItem';

export const EditRightToWorkForm = ({ onFormDirtyChange }: DirtyFormProps) => {
  const { t } = useTranslations(translations);

  const { zone, language, isUnifiedSite } = useAppConfig();

  const [isAdding, setIsAdding] = useState(false);

  const { displayedRightsToWork, setDisplayedRightsToWork, loading, error } =
    useRightsToWork();

  const {
    error: rightToWorkDropdownOptionsError,
    refetch: refetchRightToWorkDropdownOptions,
  } = useRightToWorkOptionsQuery();

  const [updateRightsToWork] = useUpdateRightsToWork();

  const { updateRightsToWorkAnalytics } = useUpdateRightsToWorkAnalytics();

  const [trackingActions, setTrackingActions] = useState<
    RightToWorkTrackingData[]
  >([]);

  const addTrackingAction = ({
    actionName,
    rtwCountry,
  }: RightToWorkTrackingData) => {
    setTrackingActions((oldList) => [
      ...oldList,
      {
        actionName,
        rtwCountry,
      },
    ]);
  };

  const { rightToWorkAddCountryPressed } = useAnalytics();

  const rightToWorkRef = useFocus<HTMLSelectElement>({
    card: RIGHT_TO_WORK,
    fieldName: 'right-to-work',
    domReady: !loading && !error,
  });

  useOnFormDirtyChange({
    onFormDirtyChange,
    dirty: Boolean(trackingActions.length),
  });

  const enterReadMode = useEnterReadMode();

  const handleUpdateRightsToWork = ({
    actionName,
    id,
    country,
    type2,
  }: HandleUpdateRightsToWorkProps) => {
    const { name: countryName, countryCode } = country;

    if (actionName === 'DELETE') {
      setDisplayedRightsToWork((oldList) =>
        removeDisplayedRightToWork({ countryCode, rightToWorkList: oldList }),
      );
    } else {
      setDisplayedRightsToWork((oldList) =>
        updateDisplayedRightToWork(oldList, {
          country,
          type2,
          id,
          isDeprecated: false,
          credential: null,
          verificationUrl: null,
        }),
      );
    }

    addTrackingAction({ actionName, rtwCountry: countryName });
  };

  const handleOnSubmit = (
    event: React.MouseEvent<HTMLFormElement, MouseEvent>,
  ) => {
    event.preventDefault();

    if (trackingActions.length) {
      const inputList = mapRightsToWorkIntoInputs(displayedRightsToWork);

      updateRightsToWork({
        input: { rightsToWork: inputList },
        zone,
        languageCode: language,
        isUnifiedSite,
      });
      updateRightsToWorkAnalytics(trackingActions);
    }
    enterReadMode();
  };

  return (
    <Stack space="xxlarge">
      {rightToWorkDropdownOptionsError ? (
        <TaxonomyOptionsErrorAlert
          refetch={refetchRightToWorkDropdownOptions}
        />
      ) : (
        <Stack space="large">
          <Box paddingTop="medium">
            <Heading level="4">{t('Your citizenship and visas')}</Heading>
          </Box>
          <form>
            <Stack space="large" data={{ automation: 'right-to-work' }}>
              {displayedRightsToWork.map((rightToWork, index) => {
                const commonProps = {
                  rightToWork,
                  handleUpdateRightsToWork,
                  ...(index === 0 ? { rightToWorkRef } : {}),
                };

                return (
                  <RightToWorkItem
                    {...commonProps}
                    key={`rtw-${rightToWork.country.countryCode}`}
                  />
                );
              })}
            </Stack>
          </form>

          {isAdding ? (
            <RightToWorkForm
              onCancel={() => setIsAdding(false)}
              onSubmit={({
                country,
                type2,
              }: {
                country: CountryInformation;
                type2: RightToWorkType2Input;
              }) => {
                handleUpdateRightsToWork({
                  actionName: 'ADD',
                  country,
                  id: createGuid(),
                  type2,
                });

                setIsAdding(false);
              }}
              zone={zone}
              existingCountryCodes={displayedRightsToWork.map(
                (country) => country.country.countryCode,
              )}
            />
          ) : (
            <Inline space="small">
              <Bleed horizontal="small">
                <Button
                  icon={<IconAdd />}
                  variant="transparent"
                  onClick={() => {
                    rightToWorkAddCountryPressed();
                    setIsAdding(true);
                  }}
                  data={{ automation: 'add-rtw-country-button' }}
                >
                  {t('Add country or region')}
                </Button>
              </Bleed>
            </Inline>
          )}

          <FormActionButtons
            cardName={t('Right to work')}
            onSubmit={handleOnSubmit}
            dataAutomationSuffix="right-to-work"
            onCancel={enterReadMode}
          />
        </Stack>
      )}
    </Stack>
  );
};

export const EditRightToWork = (dirtyFormProps: DirtyFormProps) => (
  <EditRightToWorkForm {...dirtyFormProps} />
);
